/** Table cells for the Conversion plan page. */
import { Globe, Plug, Users, Eye, MousePointerClick, Sparkles, type LucideIcon } from "lucide-react";
import type { Discovered, DiscoveredLinkingPage } from "../api";
import {
  CONVERSION_STATE_LABEL, DOC_TYPE_LABEL, PRIORITY_META, PriorityPill, effectivePriority, isOutOfDate,
} from "./planMeta";

const LINK_SOURCE: Record<string, { label: string; icon: LucideIcon }> = {
  "wp-plugin": { label: "WordPress plugin", icon: Plug },
  crawl: { label: "Crawler", icon: Globe },
  visitor: { label: "Visitor", icon: Users },
};

function LinkSourceIcon({ source }: { source?: string }) {
  const meta = LINK_SOURCE[source || "crawl"] ?? LINK_SOURCE.crawl;
  const Icon = meta.icon;
  return (
    <span title={`Seen by: ${meta.label}`} className="inline-flex shrink-0">
      <Icon className="h-3 w-3 text-slate-400" aria-hidden={true} />
      <span className="sr-only">({meta.label})</span>
    </span>
  );
}

export function PriorityCell({ r }: { r: Discovered }) {
  const eff = effectivePriority(r);
  const overridden = !!r.priority_override;
  const computed = r.priority ?? "unscored";
  return (
    <div className="flex flex-col items-start gap-1">
      <span title={PRIORITY_META[eff]?.help}><PriorityPill priority={eff} /></span>
      <span className="text-[11px] text-slate-500 tabular-nums">
        {r.priority_score != null && <>score {r.priority_score}</>}
        {overridden && (
          <span
            className="ml-1 text-brand-700 font-medium"
            title={`Set manually. Computed priority: ${PRIORITY_META[computed]?.label ?? computed}`}
          >
            · manual<span className="sr-only"> (computed: {PRIORITY_META[computed]?.label ?? computed})</span>
          </span>
        )}
      </span>
    </div>
  );
}

export function DocumentCell({ r, canonical, mirrors }: {
  r: Discovered;
  /** The canonical row when this is a duplicate and it's on the current page. */
  canonical: Discovered | null;
  /** Other rows on the current page that are duplicates of this one. */
  mirrors: Discovered[];
}) {
  const title = r.pdf_title || r.external_title || r.filename;
  return (
    <div className="min-w-0">
      <div className="font-medium text-slate-800 truncate" title={title}>{title}</div>
      {title !== r.filename && <div className="text-xs text-slate-600 truncate" title={r.filename}>{r.filename}</div>}
      <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 truncate block hover:underline" title={r.url}>
        {r.url.replace(/^https?:\/\//, "")}<span className="sr-only"> (opens in a new tab)</span>
      </a>
      {r.duplicate_of && (
        <div className="text-[11px] text-violet-700 mt-0.5 truncate">
          {canonical ? (
            <>Same file as{" "}
              <a href={canonical.url} target="_blank" rel="noopener noreferrer" className="hover:underline" title={canonical.url}>
                {canonical.filename}
              </a>
            </>
          ) : "Same file as another PDF on this site"}
        </div>
      )}
      {mirrors.length > 0 && (
        <details className="text-[11px] text-slate-500 mt-0.5">
          <summary className="cursor-pointer hover:text-slate-700">Same file also at {mirrors.length} other URL{mirrors.length === 1 ? "" : "s"}</summary>
          <ul className="mt-1 pl-3 space-y-0.5">
            {mirrors.map((m) => (
              <li key={m.id} className="truncate">
                <a href={m.url} target="_blank" rel="noopener noreferrer" className="hover:underline" title={m.url}>{m.url.replace(/^https?:\/\//, "")}</a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function TypeCell({ r }: { r: Discovered }) {
  if (!r.doc_type) return <span className="text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {DOC_TYPE_LABEL[r.doc_type] ?? r.doc_type}
      {r.doc_type_source === "ai" && (
        <span title="Type suggested by AI from the first page" className="inline-flex items-center text-[10px] font-semibold text-violet-700 bg-violet-50 rounded px-1">
          <Sparkles className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" />AI
          <span className="sr-only"> (type suggested by AI)</span>
        </span>
      )}
      {r.has_form && r.doc_type !== "form" && (
        <span className="text-[10px] text-slate-500" title="Has fillable form fields">· fillable</span>
      )}
    </span>
  );
}

export function EvidenceCell({ r }: { r: Discovered }) {
  const pages: DiscoveredLinkingPage[] = r.linking_pages ?? [];
  const views = r.page_views ?? 0;
  const opens = r.pdf_opens ?? 0;
  return (
    <div className="text-xs text-slate-600 space-y-0.5 max-w-[240px]">
      {pages.length === 0 ? (
        <div className="text-slate-400">No linking pages found</div>
      ) : (
        <details>
          <summary className="cursor-pointer hover:text-slate-800">
            Linked from {pages.length} page{pages.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-1 pl-1 space-y-0.5">
            {pages.slice(0, 20).map((lp, i) => (
              <li key={`${lp.url}-${i}`} className="flex items-center gap-1 min-w-0">
                <LinkSourceIcon source={lp.source} />
                <a href={lp.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline text-slate-500" title={lp.url}>
                  {lp.title || lp.url.replace(/^https?:\/\//, "")}
                </a>
              </li>
            ))}
            {pages.length > 20 && <li className="text-slate-400">…and {pages.length - 20} more</li>}
          </ul>
        </details>
      )}
      {(views > 0 || opens > 0) && (
        <div className="flex items-center gap-2 text-slate-500">
          {views > 0 && <span className="inline-flex items-center gap-0.5" title="Visitor page views where this PDF was linked"><Eye className="h-3 w-3" aria-hidden="true" />{views.toLocaleString()} views</span>}
          {opens > 0 && <span className="inline-flex items-center gap-0.5" title="Visitor opens of this PDF"><MousePointerClick className="h-3 w-3" aria-hidden="true" />{opens.toLocaleString()} opens</span>}
        </div>
      )}
    </div>
  );
}

export function ReasonsCell({ r }: { r: Discovered }) {
  const reasons = r.priority_reasons ?? [];
  if (reasons.length === 0) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <ul className="flex flex-wrap gap-1 max-w-[260px]" aria-label="Reasons">
      {reasons.map((x, i) => (
        <li key={i} className="inline-flex px-1.5 py-0.5 text-[11px] rounded bg-slate-100 text-slate-700">{x}</li>
      ))}
    </ul>
  );
}

export function StatusCell({ r }: { r: Discovered }) {
  const s = r.conversion_state;
  if (isOutOfDate(r) && s !== "converting" && r.status !== "selected") {
    return (
      <span className="pill-review" title="The file was replaced after it was converted. Visitors get the plain PDF until it is re-converted.">
        Out of date
      </span>
    );
  }
  if (s === "review") {
    const reasons = r.review_reasons ?? [];
    if (reasons.length === 0) return <span className="pill-review">{CONVERSION_STATE_LABEL.review}</span>;
    return (
      <details className="text-xs">
        <summary className="cursor-pointer list-none" title={reasons.join("\n")}>
          <span className="pill-review">{CONVERSION_STATE_LABEL.review}</span>
          <span className="sr-only"> — show reasons</span>
        </summary>
        <ul className="mt-1 pl-3 list-disc text-slate-600 max-w-[220px]">
          {reasons.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </details>
    );
  }
  if (s === "live") return <span className="pill-succeeded">{CONVERSION_STATE_LABEL.live}</span>;
  if (s === "converting") return <span className="pill-running">{CONVERSION_STATE_LABEL.converting}</span>;
  if (s === "failed") return <span className="pill-failed">{CONVERSION_STATE_LABEL.failed}</span>;
  if (r.status === "selected") return <span className="pill-selected">Queued</span>;
  if (r.status === "ignored") return <span className="pill-ignored">Ignored</span>;
  if (r.status === "converted") return <span className="pill-converted">Converted</span>;
  return <span className="pill-discovered">Not converted</span>;
}
